"use client";

import { baseApi } from "./baseApi";

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Ask for a reset code. Answers the same whether or not the address has an
     * account, so the response can't be read as "this person is registered".
     */
    forgotPassword: builder.mutation<
      { requested: boolean; expires_in_minutes: number },
      { email_or_username: string }
    >({
      query: (body) => ({
        url: "/authentication/forgot-password",
        method: "POST",
        body,
      }),
    }),

    /** Trade the mailed code for a new password. */
    resetPassword: builder.mutation<
      { reset: boolean },
      { email_or_username: string; reset_code: string; new_password: string }
    >({
      query: (body) => ({
        url: "/authentication/reset-password",
        method: "POST",
        body,
      }),
    }),

    /**
     * Password step. With the gateway's OTP flow enabled this no longer
     * returns a session — the response is `{ otp_required: true, otp_token }`
     * and the agent payload only arrives from `verifyLoginOtp`. With it
     * disabled the agent payload comes straight back, as it always did.
     */
    login: builder.mutation({
      query: (credential: {
        agent_email_or_username: string;
        agent_password: string;
        /** Cloudflare Turnstile token; sent only when the widget is enabled. */
        captcha_token?: string;
      }) => ({
        url: "/authentication/agentLogin",
        method: "POST",
        body: credential,
      }),
    }),

    /**
     * Answers the code. On success the body is identical to what a password-only
     * agentLogin returns, so it can be handed straight to establishSession.
     */
    verifyLoginOtp: builder.mutation({
      query: (payload: { otp_token: string; activation_code: string }) => ({
        url: "/authentication/agentLoginOtp/verify",
        method: "POST",
        body: payload,
      }),
    }),

    /** New code for the same login attempt; the previous one stops working. */
    resendLoginOtp: builder.mutation({
      query: (payload: { otp_token: string }) => ({
        url: "/authentication/agentLoginOtp/resend",
        method: "POST",
        body: payload,
      }),
    }),

    /**
     * Single sign-on: exchange a token minted by the host site for a dashboard
     * session, so an agent already signed in to the customer's own panel does
     * not log in a second time.
     *
     * The token comes from one of two places, and the gateway accepts either:
     * the host site asks us to mint one with the OAuth credentials it already
     * has (POST /api/external/sso/ticket), or it signs one itself with the
     * company's `tenant_secret`. Both are short-lived and single-use — an agent
     * session grants access to every conversation, so unlike the widget's
     * visitor flow a non-expiring identity hash is not acceptable here.
     * Contract: docs/agent-sso.md.
     *
     * Responds with the same agent payload as agentLogin, plus `msrp_ha1`: the
     * stored `h1_<md5hex>` digest credential that lets the SCont handshake run
     * without the user's password ever reaching the browser.
     */
    ssoLogin: builder.mutation({
      query: (payload: { token: string }) => ({
        url: "/authentication/agentSsoLogin",
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useSsoLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyLoginOtpMutation,
  useResendLoginOtpMutation,
} = authApi;
